import * as React from 'react';
import {
  BarSeries,
  ValueAxis,
  ScatterSeries,
  Tooltip,
  LineSeries,
} from '@devexpress/dx-react-chart-material-ui';
import {
  BarSeries as BarSeriesBase,
  ScatterSeries as ScatterSeriesBase,
  ValueAxis as ValueAxisBase,
  LineSeries as LineSeriesBase,
  ModifyDomainFn,
  FactoryFn,
  ScaleObject,
} from '@devexpress/dx-react-chart';

import { ReactElement, CSSProperties } from 'react';
import { black, purple, white } from 'globals/colors';

import { scaleTime } from 'd3-scale';
import {
  FsChartTooltipArrow,
  FsChartTooltipContent,
} from './graph-utils.style-elements';
import { formatDate, getCurrency, getDateDiffInDays } from './utils';

interface GetBarSeriesPointComponent {
  rx?: number;
  ry?: number;
}

export const getBarSeriesPointComponent = (
  props?: Partial<BarSeriesBase.PointProps> & GetBarSeriesPointComponent
) => (pointProps: BarSeriesBase.PointProps): ReactElement => {
  const isValuePositive = pointProps.value > 0;
  const newHeight = isValuePositive
    ? (pointProps.startVal - pointProps.val) / 2
    : (pointProps.val - pointProps.startVal) / 2;
  const newY = isValuePositive
    ? pointProps.val + newHeight
    : pointProps.startVal;
  const newMaxBarWidth = props?.maxBarWidth
    ? props?.maxBarWidth
    : pointProps?.maxBarWidth;
  const newProps = { height: newHeight, y: newY, maxBarWidth: newMaxBarWidth };
  return (
    <>
      <BarSeries.Point {...{ ...pointProps, ...props }} />
      <BarSeries.Point {...pointProps} {...newProps} />
    </>
  );
};

interface GetSimpleBarSeriesPointComponent {
  rx?: number;
  ry?: number;
}

export const getTopRoundBarSeriesPointComponent = (
  props?: Partial<BarSeriesBase.PointProps> & GetSimpleBarSeriesPointComponent
) => (pointProps: BarSeriesBase.PointProps): ReactElement => {
  const isValuePositive = pointProps.value > 0;
  const width = props?.barWidth;
  const translateX = width ? `translateX(-${width / 2}px)` : undefined;
  const newHeight = isValuePositive
    ? (pointProps.startVal - pointProps.val) / 2
    : (pointProps.val - pointProps.startVal) / 2;
  const newY = isValuePositive
    ? pointProps.val + newHeight
    : pointProps.startVal;
  const newProps = { height: newHeight, y: newY };
  return (
    <>
      <BarSeries.Point
        {...pointProps}
        rx={props?.rx}
        ry={props?.ry}
        style={{ width, transform: translateX }}
      />
      <BarSeries.Point
        {...pointProps}
        {...newProps}
        style={{ width, transform: translateX }}
      />
    </>
  );
};

export const getDoubleRoundBarSeriesPointComponent = (
  props?: Partial<BarSeriesBase.PointProps> & GetSimpleBarSeriesPointComponent
) => (pointProps: BarSeriesBase.PointProps): ReactElement => {
  const width = props?.barWidth;
  const translateX = width ? `translateX(-${width / 2}px)` : undefined;
  const translateY = `translateY(-2px)`;
  const transform = `${translateX} ${translateY}`;
  return (
    <>
      <BarSeries.Point
        {...pointProps}
        rx={props?.rx}
        ry={props?.ry}
        style={{ width, transform }}
      />
      <BarSeries.Point
        {...pointProps}
        rx={props?.rx}
        ry={props?.ry}
        style={{ width, transform: translateX }}
      />
    </>
  );
};

export interface GetScatterSeriesPointComponent {
  pointSize?: number;
  bgPointSize?: number;
  color?: string;
  bgColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
}

export const getScatterSeriesPointComponent = (
  props?: Partial<ScatterSeriesBase.PointProps> & GetScatterSeriesPointComponent
) => (pointProps: ScatterSeriesBase.PointProps): ReactElement => (
  <>
    <ScatterSeries.Point
      {...pointProps}
      point={{ size: props?.bgPointSize ? props?.bgPointSize : 17 }}
      color={props?.bgColor ? props?.bgColor : white}
    />
    <ScatterSeries.Point
      {...pointProps}
      point={{
        size: props?.pointSize ? props?.pointSize : 10,
      }}
      color={props?.color ? props?.color : white}
      style={{
        stroke: props?.strokeColor ? props?.strokeColor : purple.BRAND,
        strokeWidth:
          props?.strokeWidth || props?.strokeWidth === 0
            ? props?.strokeWidth
            : 5,
      }}
    />
  </>
);

export const getLineSeriesComponent = (
  props?: Partial<LineSeriesBase.SeriesProps> & CSSProperties
) => (lineSeriesProps: LineSeriesBase.SeriesProps): ReactElement => (
  <LineSeries.Path {...lineSeriesProps} {...props} />
);

export interface GetValueAxisLabelComponent {
  color?: string;
}

export const getValueAxisLabelComponent = (
  props?: Partial<ValueAxisBase.LabelProps> & GetValueAxisLabelComponent
): React.ComponentType<ValueAxisBase.LabelProps> => (
  labelProps: ValueAxis.LabelProps
): ReactElement | null => {
  return labelProps.text || labelProps.text === 0 ? (
    <ValueAxis.Label
      {...labelProps}
      style={{
        fill: props?.color,
      }}
    />
  ) : null;
};

export interface GetValueAxisGridComponent {
  strokeColor?: string;
  strokeDasharray?: number[];
}

export const getValueAxisGridComponent = (
  props?: Partial<ValueAxisBase.LineProps> & GetValueAxisGridComponent
) => (lineProps: ValueAxis.LineProps): ReactElement => {
  return (
    <ValueAxis.Grid
      {...lineProps}
      style={{
        stroke: props?.strokeColor,
        strokeDasharray:
          props?.strokeDasharray && props?.strokeDasharray.join(','),
      }}
    />
  );
};

export const getTooltipSheetComponent = (
  props?: Partial<Tooltip.SheetProps>
) => (coreProps: Tooltip.SheetProps): ReactElement => {
  return (
    <Tooltip.Sheet
      {...{ ...coreProps, ...props }}
      style={{
        background: black,
        color: white,
      }}
    />
  );
};

export const getTooltipArrowComponent = () => (): ReactElement => {
  return <FsChartTooltipArrow />;
};

export const getTooltipContentComponent = (
  props: Partial<Tooltip.ContentProps> & {
    chartData: { x: number }[];
    format?: string;
    currency?: string;
    label?: string;
  }
) => (coreProps: Tooltip.ContentProps): ReactElement => {
  const { x } = props.chartData[coreProps.targetItem.point];
  return (
    <FsChartTooltipContent
      x={x}
      format={props.format}
      currency={props.currency}
      coreProps={coreProps}
      label={props.label}
    />
  );
};

export const getAmountTooltipContentComponent = ({
  currency,
  format,
}: Partial<Tooltip.ContentProps> & {
  currency: string;
  format: string;
}) => (coreProps: Tooltip.ContentProps): ReactElement => {
  const { text } = coreProps;
  const value = parseFloat(text);
  return (
    <Tooltip.Content
      {...coreProps}
      text={getCurrency({ currency, format, value })}
    />
  );
};

type TickFormatter = (tick: string) => string;

export const amountTickFormatterForValueAxis = ({
  format,
  currency,
}: {
  format: string;
  currency: string;
}) => (): TickFormatter => (tick: string): string => {
  const value = Number(tick);
  return getCurrency({ currency, format, value });
};

export const dateTickFormatterForArgumentAxis = (): TickFormatter => (
  tick: string
): string => {
  const tickNumber = Number(tick);
  return formatDate(tickNumber, true);
};

export const domainModifier = (): ModifyDomainFn => ([
  start,
  end,
]): number[] => {
  const firstDay = new Date(start);
  const lastDay = new Date(end);
  const dateDiffInDays = Math.abs(getDateDiffInDays(start, end));
  const dateMargin = dateDiffInDays * 0.05;
  const newStart = firstDay.setDate(firstDay.getDate() - dateMargin);
  const newEnd = lastDay.setDate(lastDay.getDate() + dateMargin);
  return [newStart, newEnd];
};

export const scaleDateFactory = (): FactoryFn => (): ScaleObject => {
  return scaleTime();
};
